import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import { InvoiceStanding } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceStanding";
import { InvoiceState } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceState";
import { sumOf } from "@/Helpers/sumOf";
import { invoicesSignFactor } from "../Exports/Helpers/invoicesTotalSpend";

export function invoicesIsUnpaid(invoice: Invoice): boolean {
  return (
    invoice.invoiceState !== InvoiceState.Paid &&
    invoice.invoiceState !== InvoiceState.Voided
  );
}

export function invoicesIsIssued(invoice: Invoice): boolean {
  return invoice.invoiceState === InvoiceState.Unpaid;
}

/**
 * What the customer still owes: everything not settled or voided, less anything
 * in dispute and anything already paid.
 *
 * Nothing is collectible while a dispute is open, so counting one here asks the
 * customer for money we have agreed not to chase yet (ENG-6447). Payment sent is
 * out for the opposite reason - they have already paid it, and following up on a
 * payment we have not received is our job. Both keep their own tiles and their
 * rows stay in the table, so neither is hidden; they just aren't owed.
 *
 * Refunds subtract, the same as in the monthly Total Spend headers, so an open
 * refund lowers what they owe.
 *
 * The account statement email computes the same figure the same way, so the two
 * surfaces cannot show a customer different numbers under one label.
 */
export function invoicesTotalOpen(invoices: Invoice[]): number {
  return sumOf(
    invoices.filter(
      (invoice) =>
        invoicesIsUnpaid(invoice) &&
        invoice.standing !== InvoiceStanding.Disputed &&
        invoice.standing !== InvoiceStanding.PaymentSent,
    ),
    (invoice) => invoicesSignFactor(invoice) * invoice.amount,
  );
}

/**
 * Past due and not in dispute.
 *
 * Reads each invoice's server-computed standing rather than comparing due
 * dates, so it agrees with the rule that gates booking — the screen used to
 * work this out itself and disagreed, which is how a customer came to be shown
 * a red overdue balance while nothing was blocked (ENG-6896). Summing the same
 * invoice list as the other totals is what keeps the tiles beside each other
 * like-for-like.
 */
export function invoicesTotalOverdue(invoices: Invoice[]): number {
  return sumOf(
    invoices.filter((invoice) => invoice.standing === InvoiceStanding.Overdue),
    (invoice) => invoicesSignFactor(invoice) * invoice.amount,
  );
}

/** In dispute, so on hold with us and excluded from overdue. */
export function invoicesTotalDisputed(invoices: Invoice[]): number {
  return sumOf(
    invoices.filter((invoice) => invoice.standing === InvoiceStanding.Disputed),
    (invoice) => invoicesSignFactor(invoice) * invoice.amount,
  );
}

/** Paid by bank transfer, waiting on the debit to confirm. */
export function invoicesTotalPending(invoices: Invoice[]): number {
  return sumOf(
    invoices.filter(
      (invoice) => invoice.invoiceState === InvoiceState.PaymentSent,
    ),
    (invoice) => invoicesSignFactor(invoice) * invoice.amount,
  );
}

/**
 * Whether the "Total open" tile would just repeat one of the tiles beside it.
 *
 * The screen used to check this against the overdue total alone. The disputed
 * tile needs the same guard: a customer whose only outstanding invoice is in
 * dispute would otherwise see the identical amount twice, once as "In dispute"
 * and once as "Total open" - a figure they can't account for, which is the
 * confusion this screen was rebuilt to remove (ENG-6896).
 */
export function invoicesTotalOpenRepeatsAnotherTile(
  invoices: Invoice[],
): boolean {
  const totalOpen = invoicesTotalOpen(invoices);

  return [
    invoicesTotalOverdue(invoices),
    invoicesTotalDisputed(invoices),
    invoicesTotalPending(invoices),
  ].includes(totalOpen);
}
